/**
 * UC-A — pure helpers for the Shift Details sheet (Final PRD §16–18, §21–25, §52, §55–59). No API access (Jest-tested).
 */
import { lockMessage } from './segmentRules';

export const STATUS_LABEL = {
  DRAFT: 'Draft', OPEN: 'Open', PARTIALLY_FILLED: 'Partially filled', ASSIGNED: 'Assigned', CONFIRMED: 'Confirmed',
  IN_PROGRESS: 'In progress', COMPLETED: 'Completed', PENDING_APPROVAL: 'Pending approval', APPROVED: 'Approved',
  INVOICED: 'Invoiced', PAID: 'Paid', CANCELLED: 'Cancelled', NO_SHOW: 'Did not attend',
};

/** §59 readable status: known codes map to a label; unknown codes become sentence case, never raw SHOUT_CASE. */
export const statusLabel = (status) => {
  if (!status) return '—';
  const key = String(status).toUpperCase();
  if (STATUS_LABEL[key]) return STATUS_LABEL[key];
  const words = key.replace(/_/g, ' ').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

export const DELIVERED_STATUSES = ['COMPLETED', 'PENDING_APPROVAL', 'APPROVED', 'INVOICED', 'PAID'];
export const isDelivered = (shift) => DELIVERED_STATUSES.includes(String(shift?.status || '').toUpperCase()) || !!shift?.check_out_time;

export const toMinutes = (hhmm) => {
  if (!hhmm || typeof hhmm !== 'string' || !hhmm.includes(':')) return null;
  const [h, m] = hhmm.slice(0, 5).split(':').map(Number);
  return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null;
};

/** Minutes since the shift's own midnight; `end` rolls to the next day when the shift crosses midnight (§56 rule). */
export const shiftSpan = (shift) => {
  const s = toMinutes(shift?.start_time) ?? 0;
  let e = toMinutes(shift?.end_time) ?? s;
  const overnight = shift?.crosses_midnight === true || (shift?.end_date && shift.end_date !== shift.date) || e <= s;
  if (overnight) e += 1440;
  return { start: s, end: e, overnight };
};

/** "06:00 +1" when the minute lies past the shift's midnight. */
export const plusOne = (minutes) => {
  const days = Math.floor(minutes / 1440);
  const m = ((minutes % 1440) + 1440) % 1440;
  const hhmm = `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
  return days > 0 ? `${hhmm} +${days}` : hhmm;
};

/** Overall window label for the header: "22:00 – 06:00 +1" (UI-02, UI-22). */
export const windowLabel = (shift) => {
  const { start, end } = shiftSpan(shift);
  return `${plusOne(start)} – ${plusOne(end)}`;
};

/** UI-13 lock banner text; null when editable. */
export const lockedBanner = (locked) => (locked
  ? { title: 'Delivered records are locked', reason: lockMessage(locked), action: 'Request correction' }
  : null);

export const TABS = [
  { key: 'plan', label: 'Plan', permission: 'service_allocations.view' },
  { key: 'coverage', label: 'Coverage', permission: 'roster.view' },
  { key: 'attendance', label: 'Attendance', permission: 'roster.view' },
  { key: 'history', label: 'History', permission: 'roster.view' },
];

export const PERMISSION_REASON = {
  'service_allocations.view': 'You need the "View service allocations" permission to see the plan.',
  'service_allocations.update': 'You need the "Update service allocations" permission to edit segments — ask an administrator.',
  'service_allocations.create': 'You need the "Create service allocations" permission to add segments — ask an administrator.',
  'service_allocations.delete': 'You need the "Delete service allocations" permission to remove segments — ask an administrator.',
  'roster.view': 'You need the "View roster" permission.',
};

/** Tabs are always listed (UI-05); a tab the user cannot use is disabled with the reason, never hidden (UO-2 rendering rule). */
export const visibleTabs = (permissions = []) => {
  const has = (p) => permissions.includes(p) || permissions.includes('*') || (p.startsWith('roster.') && permissions.includes('roster.manage'));
  return TABS.map((t) => ({ ...t, enabled: has(t.permission), reason: has(t.permission) ? '' : PERMISSION_REASON[t.permission] || 'Permission required.' }));
};

export const permissionReason = (permission) => PERMISSION_REASON[permission] || 'Permission required.';

/**
 * §24–25: a worker's other booked shift is intentional only inside the same shared delivery group; everything else is an
 * accidental conflict. `statusMap` is the /availability/check-bulk payload (per worker: status, booked_shifts[]).
 */
export const accidentalConflicts = (statusMap = {}, workers = [], groupShiftIds = []) => {
  const inGroup = new Set((groupShiftIds || []).map(String));
  const out = [];
  (workers || []).forEach((w) => {
    const id = String(w.id || w.user_id || w.worker_id || '');
    const booked = (statusMap[id]?.booked_shifts || []).filter((b) => !inGroup.has(String(b.shift_id)));
    if (booked.length) {
      out.push({ worker_id: id, worker_name: w.name || w.full_name || w.worker_name || `Worker #${id.slice(-4)}`, booked,
        reason: `Also on another shift ${booked.map((b) => `${b.start_time}–${b.end_time}`).join(', ')} that is not part of this shared delivery group.` });
    }
  });
  return out;
};

/**
 * Footer Save — existing per-row save endpoints, called sequentially; stop at the first business-rule error and return it so
 * the owning row can render it inline (no batch endpoint, owner clarification 2 Oct 2026).
 */
export const savePendingRows = async (rows = []) => {
  let saved = 0;
  for (const row of rows) {
    try {
      await row.save();
      saved += 1;
    } catch (error) {
      return { saved, failed: { id: row.id, error } };
    }
  }
  return { saved, failed: null };
};
